import { ImportUsersConsts } from 'client/util/Constants';
import { get } from 'lodash';
import Api from 'api/v2/Api';
import axios from 'axios';

const userStatesUrl = '/api/v2/bulk/userstates';

class JobApi extends Api {
  /**
   * Make a call to the import results endpoint to get the results of
   * the specified job.
   * @param jobId The ID of the batch job to get the results of
   * @returns The following array of results from the Job Service:
   * [{
   *   "id": "5af095f41cf51f051ba5b19f",
   *   "status": "pending" | "started" | "in progress" | "finished" | "errored" | "canceled",
   *   "meta": {
   *     "systemUser": {
   *       "id": "5af095f4e19c180001d1a0ec",
   *       "email": "alex.dalton@workday.net",
   *       "username": "alex.dalton",
   *       "firstname": "Alex",
   *       "lastname": "Dalton",
   *       "attributes": [
   *         {
   *           "name": "WorkdayID",
   *           "value": "alex.dalton",
   *         },
   *       ],
   *     }
   *   },
   *   "createdAt": "2018-05-07T18:07:48.593Z",
   *   "updatedAt": "2018-05-07T18:07:48.129Z",
   * }, {
   *   // ...
   * }]
   */
  getImportResults(jobId) {
    const url = `/api/v2/bulk/users/${jobId}/results`;
    const params = { skip: 0, limit: 100 };
    const mesg = 'Unable to get import results!';

    return this.getAllItems(url, params, mesg);
  }

  getImportResultsDetails(jobId) {
    const url = `/api/v2/applications/import/${jobId}/detailedresults`;
    const params = {};
    const mesg = 'Unable to get import results';

    return this.getAllItems(url, params, mesg);
  }

  /**
   * Get the next scheduled job for each user in the list; for
   * now the depth = 1, i.e. only fetch one job at most for each
   * user.
   *
   * @param users An array of user ids
   * @returns An array of unsorted scheduled job objects
   *   for example:
   *     [
   *       {
   *         "scheduledJobId": "<JOB_ID>",
   *         "scheduledDate": "2021-07-26T09:00:00.000Z",
   *         "state": "SUSPENDED",
   *         "systemUserId": "<USER1>",
   *       }, {
   *         "scheduledJobId": "<JOB_ID>",
   *         "scheduledDate": "2021-07-22T07:00:00.000Z",
   *         "state": "SUSPENDED",
   *         "systemUserId": "<USER3>",
   *       }, {
   *         "scheduledJobId": "<JOB_ID>",
   *         "scheduledDate": "2021-07-29T18:00:00.000Z",
   *         "state": "ACTIVATED",
   *         "systemUserId": "<USER6>",
   *       },
   *       ...
   *     ]
   */
  getNextScheduledEvent(users = []) {
    const params = { skip: 0, limit: 100 };
    const url = `${userStatesUrl}/eventlist/next`;

    let nextEvents = [];

    /**
     * Set the slice size to half the limit.
     *
     * Reasoning: The `next` endpoint only supports a limit of 100 events
     * per call. If 100 users are passed in the users array and we get two events
     * per user, that will exceed this limit, so setting the slice size to 50
     * allows us to get the max two events (one activation, one suspension) per
     * user.
     */
    const sliceSize = params.limit / 2;
    const totalSlices = Math.ceil(users.length / sliceSize);

    // If the admin does a free text search, the `systemusers` call does
    // a post to the `search` endpoint and there may be more than limit
    // users in the list. The `next` endpoint cannot handle more than
    // 100 at a time so we need to chunk it here.
    return new Promise((resolve) => {
      const getEventsBySlice = (sliceNum) => {
        const start = (sliceNum - 1) * sliceSize;
        const end = ((sliceNum - 1) * sliceSize) + sliceSize;
        const currentSlice = users.slice(start, end);

        this.initialize()
          .then(() => {
            axios.get(`${url}?users=${currentSlice}`, { params })
              .then((resp) => {
                const results = get(resp.data, 'results', []);
                if (results.length) {
                  nextEvents = nextEvents.concat(results);
                }

                if (sliceNum < totalSlices) {
                  getEventsBySlice(sliceNum + 1);
                } else {
                  resolve(nextEvents);
                }
              })
              .catch(() => resolve(nextEvents)); // We don't care about errors
          });
      };

      getEventsBySlice(1);
    });
  }

  /**
   * Get the scheduled state changes for a given user if the `id` is provided;
   * otherwise get the scheduled state changes for all users in an organization.
   *
   * @param id The id of the user, if provided
   * @returns An array of unsorted scheduled job objects
   *   for example:
   *     [
   *       {
   *         "scheduledJobId": "<JOB_ID>",
   *         "scheduledDate": "2021-07-26T09:00:00.000Z",
   *         "state": "SUSPENDED",
   *         "systemUserId": "<USER_ID>"
   *       }, {
   *         "scheduledJobId": "<JOB_ID>",
   *         "scheduledDate": "2021-07-22T07:00:00.000Z",
   *         "state": "SUSPENDED",
   *         "systemUserId": "<USER_ID>"
   *       }, {
   *         "scheduledJobId": "<JOB_ID>",
   *         "scheduledDate": "2021-07-29T18:00:00.000Z",
   *         "state": "ACTIVATED",
   *         "systemUserId": "<USER_ID>",
   *       },
   *       ...
   *     ]
   */
  getScheduledStateChanges(id = '') {
    const params = { skip: 0, limit: 100 };
    const url = id ? `${userStatesUrl}?userid=${id}` : userStatesUrl;
    const mesg = 'Unable to get user state changes.';

    return this.getAllItems(url, params, mesg);
  }

  /**
   * This returns filters string
   * For example filter=id:eq:id
   * */
  customQueryString = (filter) => {
    let key = '';
    filter.forEach((val, index) => {
      const {
        value,
        name,
        operator,
      } = val;
      key += `${index !== 0 ? '&' : ''}filter=${name}:${operator}:${value}`;
    });
    return key;
  };

  async getUserState(skip = 0, limit = 100, filter) {
    const url = filter ? `${userStatesUrl}?${this.customQueryString(filter)}` : userStatesUrl;
    const params = { skip, limit };
    return this.initialize()
      .then(() => axios.get(url, { params })
        .then((resp) => {
          const results = (resp && resp.data) || [];
          const totalCount = parseInt(resp.headers['x-total-count'], 10);
          return { results, totalCount };
        }));
  }

  importUsers(users, source) {
    const url = '/api/v2/bulk/users';
    let headers;
    const { sourceHeader, sourceNameHeader } = ImportUsersConsts;
    if (typeof source === 'object' && source.constructor === Object) {
      /**
       * If more creationSource attributes are going to be added in the future
       * maybe create a helper function and constants to map the new attributes
       */
      headers = {
        [sourceHeader]: source.creationSource,
        [sourceNameHeader]: source.creationSourceName,
      };
    } else if (source === '') {
      // If an empty string is received, send default creation source
      headers = { [sourceHeader]: ImportUsersConsts.source.default };
    } else {
      headers = { [sourceHeader]: source };
    }

    return this
      .initialize()
      .then(() => axios.post(url, users, { headers }))
      .then(response => get(response, 'data.jobId', undefined));
  }

  createImportUsersJob(id = '', queryString, operations = []) {
    const url = `/api/v2/applications/${id}/import/jobs`;

    return this
      .initialize()
      .then(() => axios.post(url, { queryString, operations }))
      .then(response => get(response, 'data.jobId', undefined));
  }

  removeScheduledStateChange(id = '') {
    return this
      .initialize()
      .then(() => axios.delete(`${userStatesUrl}/${id}`));
  }

  /**
   * Schedule a state change for a user or list of users
   *
   * @param payload The array of users, state information and
   * the time that the job needs to run.
   *
   *   for a suspension (bulk or single user):
   *     payload: {
   *       users: ['ID_1', 'ID_2', 'ID_3',...],
   *       state: SUSPENDED,
   *       startDate: '2018-05-07T18:07:48.593Z',
   *     }
   *
   *   for bulk activation from Users page:
   *     if the admin wants to send an activation email
   *       if user state is STAGED
   *         `send_activation_emails: true` is included in payload
   *
   *   for single user activation from User aside:
   *     if the admin wants to send an activation email
   *       if user state is STAGED
   *         `send_activation_emails: true` is included in payload
   *       if user state is STAGED and admin enters email and it is different
   *       from company or alternate email
   *         `activation_email_override: <email>` is included in payload
   *
   *   @todo When one of these fields is not included in the payload, it will
   *   be undefined and it appears that axios is able to handle this. There
   *   was discussion that the undefined values should not be added to the
   *   payload in the Vuex module instead of letting axios handle it here.
   *
   * @returns An array of scheduled jobs:
   *   [{jobId: xxxx},{jobId: xxxx}]
   */
  scheduleStateChange(payload) {
    return this
      .initialize()
      .then(() => axios.post(userStatesUrl, {
        activation_email_override: payload.activation_email_override,
        send_activation_emails: payload.send_activation_emails,
        start_date: payload.startDate,
        state: payload.state,
        user_ids: payload.users,
      }))
      .then(response => get(response, 'data', undefined));
  }
}

export default new JobApi();
